const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/Medication/AdministrationPoint/AdministrationPoint.tsx';
import { css, cx } from '@emotion/css';
import moment from 'moment';
import { Typography } from '@mui/material';
import { KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

export interface IAdministrationTime {
    time: string;
    /**
     * Label to display instead of time when schedule is "schema" (e.g. Morgens)
     */
    schemaLabel?: string;
}

/**
 * An AdministrationPoint is a point in time at which medication is administered. One AdministrationPoint can
 * actually consist of a hotspot.
 */
export interface IAdministrationPoint extends IAdministrationTime {
    amountPrescribed: number;
    unitPrescribed: string;
    /**
     * If the prescribed amount of the order is ?, amountPrescribed is 0 but this flag is set to true. If the user
     * prepare/check an amount to this prescription, everything different from 0 will be showed as falsy. This
     * is intended because the order should be updated.
     */
    prescribedAmountIsActuallyUnknown?: boolean;
}

type IAdministrationPointProps = IAdministrationPoint;

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            root: css`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                text-align: center;
                padding: 0.25rem;
                width: 5rem;
                background-color: ${theme.palette.info.main} !important;
                overflow: hidden;
                font-family: inherit;
            `,
            background: css`
                background-color: ${theme.palette.info.main} !important;
            `,
            time: css`
                width: 100%;
                padding: 0;
                font-size: small;
            `,
            amount: css`
                width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
            `,
            unit: css`
                width: 100%;
                padding: 0;
                font-size: small;
            `,
        }),
        [theme]
    );
}

export const AdministrationPoint = (props: IAdministrationPointProps) => {
    const classes = useCSSClasses();
    const { time, schemaLabel, amountPrescribed, unitPrescribed, prescribedAmountIsActuallyUnknown } = props;

    const displayedTime = schemaLabel ? schemaLabel : moment(time, KISIM_TIME_FORMAT).format('LT');

    return (
        <div
            // !important at background-color is required for iOS native app (native only!)
            className={cx(classes.root, {
                [classes.background]: amountPrescribed > 0,
            })}
        >
            <Typography variant="caption" className={classes.time}>
                {displayedTime}
            </Typography>
            <div>
                <Typography variant="h5" component="span" className={cx(classes.amount)}>
                    {prescribedAmountIsActuallyUnknown && amountPrescribed === 0 ? '?' : amountPrescribed}
                </Typography>

                <Typography variant="h5" component="span" className={classes.unit}>
                    {' ' + unitPrescribed}
                </Typography>
            </div>
        </div>
    );
};
